import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtR = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

export default function PayInvoicePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const result = params.get('result');
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/public/pay/${token}`);
      setInv(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Payment link not found');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (result !== 'return') return;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/public/pay/${token}/status`);
        if (res.data.status === 'paid' || res.data.status === 'partial') { load(); clearInterval(iv); }
      } catch { /* keep polling */ }
    }, 3000);
    const stop = setTimeout(() => clearInterval(iv), 60000);
    return () => { clearInterval(iv); clearTimeout(stop); };
  }, [result, token, load]);

  const pay = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/public/pay/${token}/checkout`, { origin_url: window.location.origin });
      const form = document.createElement('form');
      form.method = res.data.method;
      form.action = res.data.action;
      res.data.fields.forEach(({ name, value }) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = name; i.value = value;
        form.appendChild(i);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start payment');
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4" data-testid="pay-invoice-page">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="bg-slate-900 text-white rounded-t-xl py-5">
          <p className="text-lg font-bold tracking-wide">IKWorx</p>
          <CardTitle className="text-sm font-normal text-slate-300">Secure Invoice Payment</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {error && <p className="text-sm text-red-600" data-testid="pay-error">{error}</p>}
          {inv && (
            <>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice</span><span className="font-mono font-medium">{inv.invoice_number}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Billed to</span><span className="font-medium">{inv.client_name}</span></div>
                {inv.due_date && <div className="flex justify-between"><span className="text-muted-foreground">Due date</span><span>{inv.due_date}</span></div>}
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{fmtR(inv.total)}</span></div>
                {inv.amount_paid > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Already paid</span><span className="text-emerald-600">{fmtR(inv.amount_paid)}</span></div>}
                <div className="flex justify-between border-t pt-2 text-base"><span className="font-semibold">Amount due</span><span className="font-bold" data-testid="pay-balance">{fmtR(inv.balance)}</span></div>
              </div>
              {inv.status === 'paid' ? (
                <div className="flex items-center gap-2 text-emerald-600 font-medium" data-testid="pay-paid-state">
                  <CheckCircle2 className="h-5 w-5" /> This invoice has been paid in full. Thank you!
                </div>
              ) : result === 'cancelled' ? (
                <>
                  <div className="flex items-center gap-2 text-amber-600 text-sm"><XCircle className="h-4 w-4" /> Payment cancelled — you can try again below.</div>
                  <Button className="w-full" onClick={pay} disabled={busy} data-testid="pay-now-btn">{busy ? 'Opening PayFast…' : `Pay ${fmtR(inv.balance)}`}</Button>
                </>
              ) : result === 'return' ? (
                <p className="text-sm text-muted-foreground" data-testid="pay-processing">Payment is being confirmed… this page updates automatically once PayFast notifies us.</p>
              ) : (
                <Button className="w-full" onClick={pay} disabled={busy} data-testid="pay-now-btn">{busy ? 'Opening PayFast…' : `Pay ${fmtR(inv.balance)}`}</Button>
              )}
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <ShieldCheck className="h-3.5 w-3.5" /> Secured by PayFast {inv.sandbox && <Badge variant="outline" className="ml-1 text-[10px]">SANDBOX TEST MODE</Badge>}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
